/**
 * Adjacency utilities for the corrective smooth deformation mode.
 *
 * The deformation mode uniforms (DQS blend, smooth factor) are now TSL
 * UniformNodes created inside gpuNormalCompute.ts and exposed on the
 * GpuNormalCompute interface. MannequinRenderer updates .value directly.
 */

/**
 * Build a flat adjacency list for Laplacian smoothing on GPU.
 *
 * Returns two arrays:
 * - offsets[vertCount+1]: for vertex i, neighbours are at adjList[offsets[i]..offsets[i+1])
 * - adjList[totalEdges]: flat array of neighbour vertex indices (canonical)
 *
 * Uses canonical vertex indices so UV-seam splits share neighbour lists.
 */
export function buildAdjacencyForSmooth(
  indexArray: ArrayLike<number>,
  canonicalMap: Uint32Array,
  vertCount: number,
): { offsets: Uint32Array; adjList: Uint32Array } {
  const neighbourSets = new Map<number, Set<number>>();
  const triCount = indexArray.length / 3;
  for (let t = 0; t < triCount; t++) {
    const c0 = canonicalMap[indexArray[t * 3]];
    const c1 = canonicalMap[indexArray[t * 3 + 1]];
    const c2 = canonicalMap[indexArray[t * 3 + 2]];
    const addEdge = (a: number, b: number) => {
      if (a === b) return;
      let s = neighbourSets.get(a);
      if (!s) {
        s = new Set();
        neighbourSets.set(a, s);
      }
      s.add(b);
    };
    addEdge(c0, c1);
    addEdge(c0, c2);
    addEdge(c1, c0);
    addEdge(c1, c2);
    addEdge(c2, c0);
    addEdge(c2, c1);
  }

  const offsets = new Uint32Array(vertCount + 1);
  let total = 0;
  for (let i = 0; i < vertCount; i++) {
    offsets[i] = total;
    const s = neighbourSets.get(i);
    total += s ? s.size : 0;
  }
  offsets[vertCount] = total;

  const adjList = new Uint32Array(total);
  for (let i = 0; i < vertCount; i++) {
    const s = neighbourSets.get(i);
    if (!s) continue;
    let idx = offsets[i];
    for (const n of s) adjList[idx++] = n;
  }

  return { offsets, adjList };
}
